import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed } from 'mobx'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import type { JobScopeChange } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { JobScopeChangeDialogViewModel } from '@/features/jobs/details/components/JobScopeChangeDialog/JobScopeChangeDialogViewModal'
import type { SnackbarFactory } from '@/features/shell'
import type { NewBillingMethodDialogViewModel } from '@/features/billing/profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import type { CheckHasBillingMethodFn } from '@/features/billing'
import type { GetCurrentMemberFn } from '@/features/iam'

/**
 * Manages the display of job scope changes.
 */
export class JobScopeChangesDisplayViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * State for when displaying as a modal.
   */
  readonly modalJobScopeChangeVm: JobScopeChangeDialogViewModel

  constructor(
    public readonly job: SubmittedJob,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    newSnackbar: SnackbarFactory,
    newBillingMethodDialogVm: NewBillingMethodDialogViewModel,
    checkHasBillingMethod: CheckHasBillingMethodFn,
    getCurrentMember: GetCurrentMemberFn,
  ) {
    this.modalJobScopeChangeVm = new JobScopeChangeDialogViewModel(
      this.job,
      this._jobScopeChangeStore,
      newSnackbar,
      checkHasBillingMethod,
      getCurrentMember,
      newBillingMethodDialogVm,
    )
    this._lifecycle.manage(this.modalJobScopeChangeVm)
  }

  /**
   * The list of {@link JobScopeChange} for the job.
   */
  @computed
  get jobScopeChanges() {
    const jobScopeChanges = this._jobScopeChangeStore.forJobSortedByDate(
      this.job.id,
    )

    const promptClientApproval =
      this.job.workspace.config.combinedPricing.jobScopePromptClientApproval

    return promptClientApproval
      ? jobScopeChanges.filter(
          (jobScopeChange) => jobScopeChange.isAdminApproved,
        )
      : jobScopeChanges.filter((x) => x.status === 'Accepted')
  }

  /**
   * The pending {@link JobScopeChange}.
   */
  @computed
  get pendingJobScopeChange() {
    return (
      this.jobScopeChanges.find((x) => x.status === 'PendingClientApproval') ??
      null
    )
  }

  /**
   * The list of {@link JobScopeChangeItem}.
   */
  @computed
  get jobScopeChangeItems() {
    return this.jobScopeChanges.flatMap((x) => x.items)
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()

    void this._fetchScopeChangeRequestsForJob()
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the {@link JobScopeChange} in a modal.
   */
  show(jobScopeChange: JobScopeChange) {
    void this.modalJobScopeChangeVm.show(jobScopeChange)
  }

  /**
   * Fetches the list of {@link JobScopeChange} for the job.
   */
  private async _fetchScopeChangeRequestsForJob(signal?: AbortSignal) {
    await this._jobScopeChangeStore.fetchScopeChangesForJob({
      jobId: this.job.id,
      signal,
    })
  }
}
